import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { MelodicCurvePlayer } from "@/components/et/MelodicCurvePlayer";
import { NextUp } from "@/components/Callouts";

export default function EarTraining5() {
  const { lesson, course, accent } = findLesson("ear-training", "5");
  return (
    <LessonShell
      course={course}
      courseSlug="ear-training"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/ear-training/ET5-Melodic-Curves-1-Animated.mp4"
        caption="Twinkle Twinkle as a pitch curve — read the shape, not the pitches"
        hint="≈ 28 sec · watch first"
      />
      <Chapter number="05" eyebrow="Ear Training · Chapter Five" title="Melodic curves — the picture before the notes">
        <Prose>
          <p>
            Before you write a single note name, sketch the <strong>shape</strong> of the
            melody. Pitch goes up, pitch goes down, pitch stays the same. That's it. Every
            melody, no matter how complex, can be sketched as a curve where time runs left
            to right and pitch goes top to bottom.
          </p>
          <p>
            Jason calls this the <strong>melodic curve</strong>, and it's the secret
            shortcut behind almost every transcription job. The curve takes thirty seconds
            to draw. Filling in the actual note names then becomes a relatively easy
            interval-counting exercise.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/05a-melodic-curves-high-low-same.png"
          caption="The three primitives of a melodic curve: a note that goes Higher than the previous one, a note that goes Lower, or a note that's the Same. Combine them across time and you have the silhouette of any melody."
        />
      </Chapter>

      <Chapter eyebrow="The transcription pipeline" title="From curve to staff in five steps">
        <Prose>
          <p>
            The full process for transcribing a melody by ear has five steps, and the
            curve is step one. Skip step one and the rest collapses; do it well and the
            rest is mechanical.
          </p>
        </Prose>

        <NoteImage
          src="/notes/ear-training/finals/05b-transcription-pipeline.png"
          caption="Jason's transcription pipeline: Melodic Curves → Scale & Key (root) → Intervals → Rhythm (note values) → Staff Notation. Each step builds on the last. Skip the curve and you'll spend twice as long on intervals."
        />

        <Steps>
          <Step><strong>Sketch the curve.</strong> Listen to the phrase 4-5 times and draw a continuous line that goes up when pitch goes up, down when pitch goes down, level when pitch repeats. Just the silhouette.</Step>
          <Step><strong>Find the key & root.</strong> Use the chapter-1 method — find the tonic, decide major or minor.</Step>
          <Step><strong>Identify intervals.</strong> Working through the curve from left to right, label each leap by its interval (P5, M3, etc.) using your ear-training drills from chapter 4.</Step>
          <Step><strong>Mark the rhythm.</strong> For each note, decide if it's a quarter, eighth, sixteenth, or held. The rhythm is independent of pitch.</Step>
          <Step><strong>Write it on the staff.</strong> Now you have everything — pitch, key, intervals, rhythm — and you can transcribe to standard notation.</Step>
        </Steps>
      </Chapter>

      <Chapter eyebrow="Try it" title="A five-note curve in A major">
        <Prose>
          <p>
            Below is a real melodic curve from Jason's notebook. Click any dot to hear it,
            or play the whole curve to hear how the silhouette translates into pitches.
            Then try sketching your own — listen to a melody, draw your curve, and check
            against the recording.
          </p>
        </Prose>

        <MelodicCurvePlayer
          notes={[
            { pitch: "A4" },
            { pitch: "B4" },
            { pitch: "C#5" },
            { pitch: "B4" },
            { pitch: "A4" },
          ]}
          title="A simple A-major opening"
        />

        <MelodicCurvePlayer
          notes={[
            { pitch: "C4" },
            { pitch: "C4" },
            { pitch: "G4" },
            { pitch: "G4" },
            { pitch: "A4" },
            { pitch: "A4" },
            { pitch: "G4" },
          ]}
          title="Twinkle Twinkle Little Star — first phrase"
        />
      </Chapter>

      <NextUp
        to="/lesson/ear-training-6"
        label="Chapter 6 · Melodic Curves 2"
        why="Now we put it into practice with three-note, four-note, and free-form melodic curves taken straight from Jason's notebook."
      />
    </LessonShell>
  );
}
